import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import {
  useDeleteStaffTaskAttachmentMutation,
  useUploadStaffTaskAttachmentMutation
} from "services/staff-tasks";
import type { StaffTaskAttachment } from "services/types/staff-tasks.type";

import {
  acceptAttribute,
  attachmentKind,
  uploadHint,
  validateAttachment
} from "../../../utils/staff-task-attachment-rules.util";

function AttachmentIcon({ filename }: { filename: string }) {
  switch (attachmentKind(filename)) {
    case "pdf":
      return <PictureAsPdfIcon color="error" fontSize="small" />;
    case "image":
      return <ImageIcon color="primary" fontSize="small" />;
    case "archive":
      return <FolderZipIcon color="action" fontSize="small" />;
    case "office":
      return <DescriptionIcon color="info" fontSize="small" />;
    default:
      return <InsertDriveFileIcon color="action" fontSize="small" />;
  }
}

const DropZone = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: "12px",
  boxShadow: `${theme.palette.common.black}19 0px 8px 24px`,
  padding: "8px",
  "&:hover, &.dragover": {
    opacity: 0.6
  }
}));

type PendingFile = {
  id: string;
  file: File;
  name: string;
};

type StaffTaskAttachmentsProps = {
  taskId: number | undefined;
  attachments?: StaffTaskAttachment[];
  readonly?: boolean;
  /**
   * Onboarding (implementation) tasks accept zip, images and Office files; every other staff task
   * stays PDF-only. Defaults to false so existing callers are unchanged. The same split is
   * enforced server-side off the task's implementation, so this is convenience, not the rule.
   */
  allowExtendedFileTypes?: boolean;
};

export type StaffTaskAttachmentsRef = {
  uploadPendingFiles: (taskId: number) => Promise<void>;
  hasPendingFiles: () => boolean;
};

const StaffTaskAttachments = forwardRef<
  StaffTaskAttachmentsRef,
  StaffTaskAttachmentsProps
>(function StaffTaskAttachments(
  {
    taskId,
    attachments = [],
    readonly = false,
    allowExtendedFileTypes = false
  },
  ref
) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadStaffTaskAttachmentMutation();
  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteStaffTaskAttachmentMutation();

  const uploadPendingFiles = useCallback(
    async (newTaskId: number) => {
      for (const pending of pendingFiles) {
        await uploadAttachment({ taskId: newTaskId, file: pending.file });
      }
      setPendingFiles([]);
    },
    [pendingFiles, uploadAttachment]
  );

  const hasPendingFiles = useCallback(() => {
    return pendingFiles.length > 0;
  }, [pendingFiles]);

  useImperativeHandle(ref, () => ({
    uploadPendingFiles,
    hasPendingFiles
  }));

  const processFile = useCallback(
    async (file: File) => {
      const problem = validateAttachment(file, allowExtendedFileTypes);
      if (problem) {
        setUploadError(problem);
        return;
      }
      setUploadError(null);

      if (taskId) {
        await uploadAttachment({ taskId, file });
      } else {
        setPendingFiles((prev) => [
          ...prev,
          {
            id: `pending-${Date.now()}-${Math.random()}`,
            file,
            name: file.name
          }
        ]);
      }
    },
    [taskId, uploadAttachment, allowExtendedFileTypes]
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      for (const file of Array.from(files)) {
        await processFile(file);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFile]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dropZoneRef.current?.classList.remove("dragover");

      const files = event.dataTransfer.files;
      for (const file of Array.from(files)) {
        await processFile(file);
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleDragEnter = useCallback(() => {
    dropZoneRef.current?.classList.add("dragover");
  }, []);

  const handleDragLeave = useCallback(() => {
    dropZoneRef.current?.classList.remove("dragover");
  }, []);

  const handleDelete = useCallback(
    async (attachmentId: number) => {
      if (!taskId) return;
      await deleteAttachment({ taskId, attachmentId });
    },
    [taskId, deleteAttachment]
  );

  const handleDeletePending = useCallback((pendingId: string) => {
    setPendingFiles((prev) => prev.filter((p) => p.id !== pendingId));
  }, []);

  const hasAttachments = attachments.length > 0 || pendingFiles.length > 0;

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Attachments</Typography>

      {!readonly && (
        <DropZone
          ref={dropZoneRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: "relative",
              width: "100%",
              height: "5rem",
              border: `1px dashed ${theme.palette.secondary.main}`,
              borderRadius: "12px",
              boxSizing: "border-box",
              cursor: "pointer"
            }}
          >
            <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
              {isUploading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Box>
                    <FileDownloadIcon color="action" />
                  </Box>
                  <Typography variant="body2">
                    {allowExtendedFileTypes
                      ? "Drop files here or click to upload"
                      : "Drop PDF files here or click to upload"}
                  </Typography>
                  <Typography variant="body1" color={grey[600]}>
                    {uploadHint(allowExtendedFileTypes)}
                  </Typography>
                </>
              )}
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptAttribute(allowExtendedFileTypes)}
              onChange={handleFileSelect}
              style={{
                opacity: 0,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer"
              }}
            />
          </Box>
        </DropZone>
      )}

      {uploadError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {uploadError}
        </Typography>
      )}

      {!hasAttachments && readonly && (
        <Typography variant="body2" color="text.secondary">
          No attachments
        </Typography>
      )}

      {hasAttachments && (
        <Stack spacing={0.5}>
          {attachments.map((attachment) => (
            <Stack
              key={attachment.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 0.5,
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "action.hover"
                }
              }}
            >
              <AttachmentIcon filename={attachment.original_filename} />
              <Link
                href={attachment.file}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {attachment.original_filename}
              </Link>
              {/*
                Only the uploader may remove a file — the server decides and says so via
                can_delete, and refuses anyone else. Older payloads omit the flag, so an absent
                value is treated as allowed and the server stays the backstop.
              */}
              {!readonly && attachment.can_delete !== false && (
                <Tooltip title="Delete attachment">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(attachment.id)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}

          {pendingFiles.map((pending) => (
            <Stack
              key={pending.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 0.5,
                borderRadius: 1,
                backgroundColor: "action.selected",
                "&:hover": {
                  backgroundColor: "action.hover"
                }
              }}
            >
              <AttachmentIcon filename={pending.name} />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {pending.name}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (pending upload)
                </Typography>
              </Typography>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => handleDeletePending(pending.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
});

export default StaffTaskAttachments;
